import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Mail,
  Heart,
  Camera,
  ShieldCheck,
  Upload,
  X,
  Check,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  OnboardingProvider,
  useOnboarding,
  setSessionProfileId,
} from "../context/OnboardingContext";
import StepShell from "../components/onboarding/StepShell";
import { PickOne, PickMany, TextField } from "../components/onboarding/StepControls";
import BrandHeart from "../components/qhearts/BrandHeart";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* --------- helpers --------- */
const ageYears = (iso) => {
  if (!iso) return 0;
  const [y, m, d] = iso.split("-").map(Number);
  const b = new Date(y, (m || 1) - 1, d || 1);
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const mDiff = now.getMonth() - b.getMonth();
  if (mDiff < 0 || (mDiff === 0 && now.getDate() < b.getDate())) age--;
  return age;
};

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");

/* File → compressed base64 data URL */
async function fileToDataUrl(file, maxDim = 1400, quality = 0.85) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    let { width, height } = img;
    if (Math.max(width, height) > maxDim) {
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function uploadPhoto(dataUrl, kind = "profile") {
  const { data } = await axios.post(`${API}/photos`, { data: dataUrl, kind });
  return data; // { id, url, kind }
}

/* --------- step components --------- */

function StepEntry() {
  const { next } = useOnboarding();
  const navigate = useNavigate();
  return (
    <StepShell
      step={1}
      showProgress={false}
      showBack={false}
      onNext={next}
      nextLabel="Konto erstellen"
      footer={
        <button
          onClick={() => navigate("/login")}
          className="w-full h-11 rounded-full border border-qh-line text-sm font-medium text-qh-ink hover:bg-[#FFFBFB] transition-colors"
          data-testid="entry-login"
        >
          Ich habe schon ein Konto · Anmelden
        </button>
      }
    >
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
        <img
          src="/assets/qhearts-logo.png"
          alt="QHearts — Keep Dating Real"
          className="h-20 sm:h-24 w-auto object-contain select-none mx-auto"
          draggable="false"
        />
        <h1 className="mt-8 font-display text-4xl sm:text-5xl font-medium tracking-tight">
          Kein Swipe, ein <span className="italic text-qh-rose">echter</span> Moment.
        </h1>
        <p className="mt-5 text-lg text-qh-slate max-w-sm">
          Finde, was dein Herz wirklich sucht. Dein nächster Moment beginnt hier.
        </p>
      </div>
    </StepShell>
  );
}

function StepEmail() {
  const { data, update, next } = useOnboarding();
  const ok = emailValid(data.email);
  return (
    <StepShell
      step={2}
      title="Deine E-Mail-Adresse."
      subtitle="Damit du dein Konto wiederfindest — wir schicken dir hierhin alles Wichtige."
      onNext={next}
      nextDisabled={!ok}
    >
      <TextField
        type="email"
        value={data.email}
        onChange={(v) => update({ email: v.toLowerCase().trim() })}
        placeholder="deine@email.de"
        autoFocus
      />
    </StepShell>
  );
}

function StepEmailConfirm() {
  const { data, next } = useOnboarding();
  const [sent, setSent] = useState(true);
  return (
    <StepShell
      step={3}
      title="Wir haben dir geschrieben."
      subtitle={`Wenn du ${data.email || "deine E-Mail"} öffnest, findest du unseren Bestätigungs-Link.`}
      onNext={next}
      nextLabel="Ich habe bestätigt · Weiter"
    >
      <div className="rounded-2xl border border-qh-line bg-[#FFFBFB] p-6 flex items-start gap-4">
        <span className="h-10 w-10 rounded-full bg-white border border-qh-line flex items-center justify-center shrink-0">
          <Mail className="w-4 h-4 text-qh-rose" strokeWidth={1.75} />
        </span>
        <div>
          <p className="text-sm text-qh-ink font-medium">
            Bestätigungs-Mail unterwegs.
          </p>
          <p className="mt-1 text-xs text-qh-slate">
            Noch nichts bekommen? Prüfe deinen Spam-Ordner oder lass die Mail erneut senden.
          </p>
          <button
            onClick={() => {
              setSent(false);
              setTimeout(() => setSent(true), 800);
              toast.success("Mail erneut gesendet.");
            }}
            className="mt-3 text-xs underline underline-offset-2 text-qh-ink hover:text-qh-rose"
          >
            {sent ? "Erneut senden" : "Wird gesendet..."}
          </button>
        </div>
      </div>
    </StepShell>
  );
}

function StepIntro() {
  const { data, update, next } = useOnboarding();
  const cards = [
    {
      num: "01",
      title: "Du siehst jemanden im echten Leben.",
      body: "Ein Moment. Ein Blick. Ein Gefühl.",
    },
    {
      num: "02",
      title: "Du gibst ein QHeart.",
      body: "Bewusst gewählt, mit deinen Worten.",
    },
    {
      num: "03",
      title: "Eine echte Verbindung entsteht.",
      body: "Ohne Druck. Ohne Ghosting. Ihre Entscheidung.",
    },
  ];
  return (
    <StepShell
      step={4}
      title="So funktioniert QHearts."
      subtitle="Nimm dir einen Moment. Hier beginnt etwas Echtes."
      onNext={next}
      nextDisabled={!data.acknowledged_intro}
    >
      <div className="space-y-3">
        {cards.map((c) => (
          <div
            key={c.num}
            className="rounded-2xl border border-qh-line bg-white p-5"
          >
            <span className="font-display text-xs text-qh-rose tracking-[0.22em]">
              {c.num}
            </span>
            <h3 className="mt-2 font-display text-xl font-medium">{c.title}</h3>
            <p className="mt-1 text-sm text-qh-slate leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
      <label className="mt-6 flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={data.acknowledged_intro}
          onChange={(e) => update({ acknowledged_intro: e.target.checked })}
          className="h-5 w-5 rounded border-qh-line accent-qh-rose"
          data-testid="intro-ack"
        />
        <span className="text-sm text-qh-ink">Ich habe verstanden.</span>
      </label>
    </StepShell>
  );
}

const GENDERS = [
  { value: "frau", label: "Frau" },
  { value: "mann", label: "Mann" },
  { value: "non-binary", label: "Non-binär" },
  { value: "divers", label: "Anders" },
];
const INTERESTED_IN = [
  { value: "frauen", label: "Frauen" },
  { value: "maenner", label: "Männer" },
  { value: "alle", label: "Alle" },
];
const RELATIONSHIP = [
  { value: "ernst", label: "Etwas Ernstes" },
  { value: "offen", label: "Offen für alles" },
  { value: "locker", label: "Locker, unverbindlich" },
  { value: "freundschaft", label: "Neue Freundschaft" },
];
const CHILDREN = [
  { value: "ohne", label: "Noch keine" },
  { value: "mit", label: "Habe Kinder" },
  { value: "manchmal", label: "Manchmal" },
];
const FAMILY = [
  { value: "ja", label: "Ja, gerne" },
  { value: "vielleicht", label: "Vielleicht" },
  { value: "nein", label: "Nein, danke" },
];
const FAITH = [
  { value: "spirituell", label: "Spirituell" },
  { value: "christlich", label: "Christlich" },
  { value: "juedisch", label: "Jüdisch" },
  { value: "muslimisch", label: "Muslimisch" },
  { value: "buddhistisch", label: "Buddhistisch" },
  { value: "hinduistisch", label: "Hinduistisch" },
  { value: "keine", label: "Keine Angabe" },
];
const INTERESTS_LIST = [
  "Musik", "Reisen", "Lesen", "Kino", "Kochen", "Kunst", "Fotografie",
  "Yoga", "Wandern", "Sport", "Gaming", "Tanzen", "Kaffee", "Wein",
  "Natur", "Tiere", "Festivals", "Podcasts",
].map((v) => ({ value: v.toLowerCase(), label: v }));
const TRAITS_LIST = [
  "Empathisch", "Ehrlich", "Humorvoll", "Neugierig", "Ruhig",
  "Abenteuerlustig", "Kreativ", "Bodenständig", "Tiefgründig",
  "Spontan", "Zuverlässig", "Warm",
].map((v) => ({ value: v.toLowerCase(), label: v }));
const LIFESTYLE = [
  { value: "aktiv", label: "Aktiv & draußen" },
  { value: "ruhig", label: "Ruhig & gemütlich" },
  { value: "kulturell", label: "Kulturell" },
  { value: "spontan", label: "Spontan" },
];
const EDU = [
  { value: "schule", label: "Schulabschluss" },
  { value: "ausbildung", label: "Ausbildung" },
  { value: "bachelor", label: "Bachelor" },
  { value: "master", label: "Master / Diplom" },
  { value: "promotion", label: "Promotion" },
];
const SMOKING = [
  { value: "nie", label: "Nie" },
  { value: "sozial", label: "Gelegentlich" },
  { value: "regelmaessig", label: "Regelmäßig" },
];
const ALCOHOL = [
  { value: "nie", label: "Nie" },
  { value: "sozial", label: "Ab und zu" },
  { value: "regelmaessig", label: "Regelmäßig" },
];

function ChoiceStep({ stepNum, title, subtitle, fieldKey, options, skipNext, isMulti, max }) {
  const { data, update, next } = useOnboarding();
  const val = data[fieldKey];
  const hasValue = isMulti ? (val || []).length > 0 : !!val;
  return (
    <StepShell
      step={stepNum}
      title={title}
      subtitle={subtitle}
      onNext={next}
      nextDisabled={!hasValue && !skipNext}
      onSkip={skipNext ? next : undefined}
    >
      {isMulti ? (
        <PickMany
          value={val || []}
          onChange={(v) => update({ [fieldKey]: v })}
          options={options}
          max={max}
        />
      ) : (
        <PickOne
          value={val}
          onChange={(v) => update({ [fieldKey]: v })}
          options={options}
        />
      )}
    </StepShell>
  );
}

function TextStep({ stepNum, title, subtitle, fieldKey, placeholder, skipNext, type = "text" }) {
  const { data, update, next } = useOnboarding();
  const val = data[fieldKey];
  return (
    <StepShell
      step={stepNum}
      title={title}
      subtitle={subtitle}
      onNext={next}
      nextDisabled={!val && !skipNext}
      onSkip={skipNext ? next : undefined}
    >
      <TextField
        type={type}
        value={val}
        onChange={(v) => update({ [fieldKey]: v })}
        placeholder={placeholder}
        autoFocus
      />
    </StepShell>
  );
}

function StepName() {
  return (
    <TextStep
      stepNum={6}
      title="Wie sollen dich andere nennen?"
      subtitle="Dein Vorname reicht — das ist, was andere sehen."
      fieldKey="name"
      placeholder="Dein Name"
    />
  );
}

function StepBirth() {
  const { data, update, next } = useOnboarding();
  const age = ageYears(data.birth_date);
  const ok = data.birth_date && age >= 18;
  return (
    <StepShell
      step={7}
      title="Dein Geburtsdatum."
      subtitle="Nur Menschen ab 18 Jahren sind bei QHearts willkommen."
      onNext={next}
      nextDisabled={!ok}
    >
      <TextField
        type="date"
        value={data.birth_date}
        onChange={(v) => update({ birth_date: v })}
        placeholder="YYYY-MM-DD"
      />
      {data.birth_date && age < 18 && (
        <p className="mt-3 text-sm text-qh-rose">
          Du musst mindestens 18 Jahre alt sein.
        </p>
      )}
    </StepShell>
  );
}

function StepHeight() {
  const { data, update, next } = useOnboarding();
  return (
    <StepShell
      step={10}
      title="Wie groß bist du?"
      subtitle="Optional. Manche mögen den Hinweis."
      onNext={next}
      onSkip={next}
      skipLabel="Überspringen"
    >
      <div className="flex items-center gap-3">
        <TextField
          type="number"
          value={data.height_cm || ""}
          onChange={(v) => update({ height_cm: v ? Number(v) : null })}
          placeholder="175"
        />
        <span className="text-qh-slate">cm</span>
      </div>
    </StepShell>
  );
}

function StepLocation() {
  const { data, update, next } = useOnboarding();
  const ok = data.city && data.city.length > 1;
  return (
    <StepShell
      step={21}
      title="Wo lebst du gerade?"
      subtitle="Damit wir dir echte Begegnungen in deiner Nähe zeigen."
      onNext={next}
      nextDisabled={!ok}
    >
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <TextField
            value={data.city}
            onChange={(v) => update({ city: v })}
            placeholder="Stadt"
          />
        </div>
        <TextField
          value={data.zip_code}
          onChange={(v) => update({ zip_code: v })}
          placeholder="PLZ"
        />
      </div>
    </StepShell>
  );
}

function StepBio() {
  const { data, update, next } = useOnboarding();
  return (
    <StepShell
      step={22}
      title="Erzähle ein wenig von dir."
      subtitle="Ein Satz, der zeigt, wer du bist — nicht dein Lebenslauf."
      onNext={next}
      onSkip={next}
      skipLabel="Später ergänzen"
    >
      <textarea
        value={data.bio || ""}
        onChange={(e) => update({ bio: e.target.value })}
        placeholder="Was würdest du jemandem beim ersten Kaffee erzählen?"
        maxLength={400}
        rows={6}
        className="w-full px-5 py-4 rounded-2xl border border-qh-line bg-white text-base focus:outline-none focus:border-qh-rose focus:ring-2 focus:ring-qh-rose/20 transition-all resize-none"
        data-testid="bio-field"
      />
      <div className="mt-2 text-xs text-qh-slate text-right">
        {(data.bio || "").length}/400
      </div>
    </StepShell>
  );
}

function StepPhotos() {
  const { data, update, next } = useOnboarding();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files).slice(0, 6)) {
        if (!file.type.startsWith("image/")) continue;
        const dataUrl = await fileToDataUrl(file);
        const res = await uploadPhoto(dataUrl, "profile");
        uploaded.push(res);
      }
      update({ photos: [...(data.photos || []), ...uploaded].slice(0, 6) });
      toast.success(`${uploaded.length} Bild${uploaded.length > 1 ? "er" : ""} hochgeladen.`);
    } catch (e) {
      toast.error("Upload fehlgeschlagen. Bitte versuch es erneut.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePhoto = (id) => {
    update({ photos: (data.photos || []).filter((p) => p.id !== id) });
  };

  const hasPhoto = (data.photos || []).length > 0;

  return (
    <StepShell
      step={23}
      title="Ein paar Bilder von dir."
      subtitle="Dein Gesicht sollte klar erkennbar sein. Mindestens ein Bild."
      onNext={next}
      nextDisabled={!hasPhoto || uploading}
    >
      <div className="grid grid-cols-3 gap-3" data-testid="photos-grid">
        {(data.photos || []).map((p) => (
          <div
            key={p.id}
            className="relative aspect-square rounded-2xl overflow-hidden border border-qh-line bg-[#FFFBFB]"
          >
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
              alt="Profilbild"
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => removePhoto(p.id)}
              className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-qh-line flex items-center justify-center hover:bg-white"
              aria-label="Entfernen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {(data.photos || []).length < 6 && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-2xl border-2 border-dashed border-qh-line hover:border-qh-rose bg-white flex flex-col items-center justify-center gap-2 text-qh-slate hover:text-qh-rose transition-all"
            data-testid="photos-upload"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span className="text-xs">Bild hinzufügen</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <p className="mt-4 text-xs text-qh-slate">
        Bis zu 6 Bilder · JPG oder PNG · Dein Gesicht sollte sichtbar sein.
      </p>
    </StepShell>
  );
}

function StepVerifyIntro() {
  const { next } = useOnboarding();
  return (
    <StepShell
      step={24}
      title="Lass dich verifizieren."
      subtitle="Mehr Vertrauen. Mehr echte Begegnungen."
      onNext={next}
      nextLabel="Jetzt verifizieren"
      onSkip={next}
      skipLabel="Später — Profil ohne Verifizierung starten"
    >
      <ul className="space-y-3 text-qh-ink">
        {[
          "Dein Profil wird überprüft.",
          "Mehr Sicherheit für alle in der Community.",
          "Höhere Chancen auf echte Begegnungen.",
        ].map((t) => (
          <li key={t} className="flex gap-3 items-start">
            <span className="shrink-0 h-6 w-6 rounded-full bg-[#FFE4E6] flex items-center justify-center mt-0.5">
              <Check className="w-3.5 h-3.5 text-qh-rose" strokeWidth={2.5} />
            </span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </StepShell>
  );
}

function StepHowVerify() {
  const { next } = useOnboarding();
  return (
    <StepShell
      step={25}
      title="So funktioniert's."
      subtitle="Nur drei kleine Schritte."
      onNext={next}
    >
      <ol className="space-y-3">
        {[
          { n: "01", t: "Selfie aufnehmen", b: "Direkt mit deiner Kamera." },
          { n: "02", t: "Gesicht wird geprüft", b: "Wir gleichen dein Selfie mit deinen Bildern ab." },
          { n: "03", t: "Profil wird bestätigt", b: "Du bekommst deinen verifizierten Status." },
        ].map((s) => (
          <li
            key={s.n}
            className="rounded-2xl border border-qh-line bg-white p-5"
          >
            <span className="font-display text-xs text-qh-rose tracking-[0.22em]">
              {s.n}
            </span>
            <div className="mt-1 font-display text-lg font-medium">{s.t}</div>
            <div className="text-sm text-qh-slate mt-0.5">{s.b}</div>
          </li>
        ))}
      </ol>
      <p className="mt-6 text-xs text-qh-slate">
        Deine Daten bleiben sicher — sie werden ausschließlich zur Verifizierung
        genutzt.
      </p>
    </StepShell>
  );
}

function StepLegalName() {
  const { data, update, next } = useOnboarding();
  const ok = data.legal_first_name && data.legal_last_name;
  return (
    <StepShell
      step={26}
      title="Dein Klarname."
      subtitle="Nur für die Verifizierung — nicht öffentlich sichtbar."
      onNext={next}
      nextDisabled={!ok}
      onSkip={next}
      skipLabel="Später ergänzen"
    >
      <div className="grid grid-cols-2 gap-3">
        <TextField
          value={data.legal_first_name}
          onChange={(v) => update({ legal_first_name: v })}
          placeholder="Vorname"
        />
        <TextField
          value={data.legal_last_name}
          onChange={(v) => update({ legal_last_name: v })}
          placeholder="Nachname"
        />
      </div>
    </StepShell>
  );
}

function StepCamPerm() {
  const { next } = useOnboarding();
  const [asking, setAsking] = useState(false);
  const [granted, setGranted] = useState(false);
  const requestCam = async () => {
    setAsking(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setGranted(true);
      setTimeout(next, 500);
    } catch (e) {
      toast.error("Ohne Kamera können wir dich leider nicht verifizieren.");
    } finally {
      setAsking(false);
    }
  };
  return (
    <StepShell
      step={27}
      title="Zugriff auf die Kamera."
      subtitle="Nur für dein Selfie — wir nehmen nichts auf, was du nicht bestätigst."
      onNext={granted ? next : requestCam}
      nextLabel={granted ? "Weiter" : asking ? "Einen Moment…" : "Kamera erlauben"}
      onSkip={next}
      skipLabel="Später verifizieren"
    >
      <div className="rounded-2xl border border-qh-line bg-[#FFFBFB] p-6 text-center">
        <Camera className="w-8 h-8 text-qh-rose mx-auto" strokeWidth={1.5} />
        <p className="mt-3 text-sm text-qh-ink">
          Dein Browser fragt dich als Nächstes, ob QHearts deine Kamera nutzen darf.
        </p>
      </div>
    </StepShell>
  );
}

function StepSelfie() {
  const { data, update, next } = useOnboarding();
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [uploaded, setUploaded] = useState(!!data.selfie);

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 720, height: 720 },
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      toast.error("Kamera nicht verfügbar.");
    }
  };

  const capture = async () => {
    if (!videoRef.current || !stream) return;
    setCapturing(true);
    try {
      const v = videoRef.current;
      const size = Math.min(v.videoWidth, v.videoHeight);
      const canvas = document.createElement("canvas");
      canvas.width = 600;
      canvas.height = 600;
      const ctx = canvas.getContext("2d");
      const sx = (v.videoWidth - size) / 2;
      const sy = (v.videoHeight - size) / 2;
      ctx.drawImage(v, sx, sy, size, size, 0, 0, 600, 600);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      const res = await uploadPhoto(dataUrl, "selfie");
      update({ selfie: res, verification_status: "pending" });
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
      setUploaded(true);
    } catch (e) {
      toast.error("Selfie konnte nicht aufgenommen werden.");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <StepShell
      step={28}
      title="Ein Selfie — für dich und für uns."
      subtitle="Halte dein Gesicht mittig in den Rahmen."
      onNext={next}
      nextDisabled={!uploaded}
      nextLabel="Weiter"
    >
      <div className="mx-auto aspect-square max-w-[320px] rounded-full overflow-hidden border-4 border-[#FFE4E6] relative bg-[#FFFBFB]">
        {uploaded && data.selfie ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${data.selfie.url}`}
            alt="Selfie"
            className="w-full h-full object-cover"
          />
        ) : stream ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-qh-slate">
            <Camera className="w-10 h-10" strokeWidth={1.25} />
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-center">
        {uploaded ? (
          <button
            onClick={() => {
              update({ selfie: null });
              setUploaded(false);
            }}
            className="text-sm text-qh-slate underline underline-offset-2"
          >
            Neu aufnehmen
          </button>
        ) : stream ? (
          <button
            onClick={capture}
            disabled={capturing}
            className="h-14 px-7 rounded-full bg-qh-rose text-white font-medium disabled:opacity-60"
            data-testid="selfie-capture"
          >
            {capturing ? "Aufnehmen…" : "Foto aufnehmen"}
          </button>
        ) : (
          <button
            onClick={start}
            className="h-14 px-7 rounded-full border border-qh-line bg-white font-medium hover:border-qh-rose"
            data-testid="selfie-start"
          >
            Kamera starten
          </button>
        )}
      </div>
    </StepShell>
  );
}

function StepVerifyStatus() {
  const { data, next, update } = useOnboarding();
  const [state, setState] = useState("checking");

  React.useEffect(() => {
    if (!data.selfie) {
      setState("skipped");
      return;
    }
    const t = setTimeout(() => {
      setState("success");
      update({ verification_status: "verified" });
    }, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  return (
    <StepShell
      step={29}
      title={
        state === "success"
          ? "Verifizierung erfolgreich."
          : state === "skipped"
          ? "Verifizierung übersprungen."
          : "Wir prüfen dein Selfie…"
      }
      subtitle={
        state === "success"
          ? "Dein Profil erhält den Verifiziert-Haken."
          : state === "skipped"
          ? "Du kannst das jederzeit aus deinem Profil heraus nachholen."
          : "Das dauert nur einen Moment."
      }
      onNext={next}
      nextLabel="Weiter"
      nextDisabled={state === "checking"}
    >
      <div className="py-8 flex flex-col items-center text-center">
        {state === "checking" && (
          <Loader2 className="w-10 h-10 animate-spin text-qh-rose" />
        )}
        {state === "success" && (
          <>
            <span className="h-16 w-16 rounded-full bg-[#FFE4E6] flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-qh-rose" strokeWidth={2} />
            </span>
            <p className="mt-4 text-sm text-qh-ink">
              <span className="font-semibold">Verifiziert.</span> Willkommen in der echten Runde.
            </p>
          </>
        )}
        {state === "skipped" && (
          <>
            <AlertTriangle className="w-8 h-8 text-qh-slate" />
            <p className="mt-3 text-sm text-qh-slate">
              Kein Selfie hochgeladen — dein Profil startet unverifiziert.
            </p>
          </>
        )}
      </div>
    </StepShell>
  );
}

function StepReview() {
  const { data, next } = useOnboarding();
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-qh-line last:border-0">
      <span className="text-sm text-qh-slate">{label}</span>
      <span className="text-sm text-qh-ink text-right max-w-[60%] break-words">
        {value || "—"}
      </span>
    </div>
  );
  const pickLabel = (list, v) => list.find((x) => x.value === v)?.label || v;
  return (
    <StepShell
      step={30}
      title="Dein Profil — passt alles?"
      subtitle="Du kannst später jederzeit Dinge anpassen."
      onNext={next}
      nextLabel="Profil erstellen"
    >
      <div className="rounded-2xl border border-qh-line bg-white p-5">
        <Row label="Name" value={data.name} />
        <Row label="Geburtsdatum" value={data.birth_date} />
        <Row label="Geschlecht" value={pickLabel(GENDERS, data.gender)} />
        <Row
          label="Interesse"
          value={(data.interested_in || [])
            .map((v) => pickLabel(INTERESTED_IN, v))
            .join(", ")}
        />
        <Row label="Beziehungstyp" value={pickLabel(RELATIONSHIP, data.relationship_type)} />
        <Row label="Stadt" value={data.city} />
        <Row label="Bio" value={data.bio} />
        <Row label="Bilder" value={`${(data.photos || []).length} hochgeladen`} />
        <Row
          label="Verifiziert"
          value={data.verification_status === "verified" ? "Ja" : "Noch nicht"}
        />
      </div>
    </StepShell>
  );
}

function StepSuccess() {
  const { data, reset, saveProfileId } = useOnboarding();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [createdId, setCreatedId] = useState(null);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const payload = { ...data };
      delete payload.acknowledged_intro;
      const res = await axios.post(`${API}/profiles`, payload);
      saveProfileId(res.data.id);
      setSessionProfileId(res.data.id);
      setCreatedId(res.data.id);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Profil konnte nicht erstellt werden. Bitte erneut versuchen."
      );
    } finally {
      setCreating(false);
    }
  };

  React.useEffect(() => {
    if (!createdId) create();
    // eslint-disable-next-line
  }, []);

  return (
    <StepShell
      step={31}
      showProgress={false}
      showBack={false}
      onNext={
        createdId
          ? () => {
              reset();
              navigate("/profile");
            }
          : undefined
      }
      nextLabel="Zum Profil"
    >
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
        {creating && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-qh-rose" />
            <p className="mt-4 text-qh-slate">Dein Profil wird erstellt…</p>
          </>
        )}
        {error && (
          <>
            <AlertTriangle className="w-8 h-8 text-qh-rose" />
            <p className="mt-4 text-qh-ink">{error}</p>
            <button
              onClick={create}
              className="mt-5 h-11 px-5 rounded-full border border-qh-line"
            >
              Erneut versuchen
            </button>
          </>
        )}
        {createdId && (
          <>
            <BrandHeart className="w-14 h-14" alt="QHearts" />
            <h1 className="mt-6 font-display text-4xl font-medium">
              Willkommen bei QHearts.
            </h1>
            <p className="mt-3 text-qh-slate">Dein nächster Moment wartet bereits.</p>
          </>
        )}
      </div>
    </StepShell>
  );
}

/* --------- wizard dispatcher --------- */

function Wizard() {
  const { step } = useOnboarding();
  switch (step) {
    case 1:
      return <StepEntry />;
    case 2:
      return <StepEmail />;
    case 3:
      return <StepEmailConfirm />;
    case 4:
      return <StepIntro />;
    case 5:
      return (
        <ChoiceStep
          stepNum={5}
          title="Dein Geschlecht."
          subtitle="So stellst du dich vor."
          fieldKey="gender"
          options={GENDERS}
        />
      );
    case 6:
      return <StepName />;
    case 7:
      return <StepBirth />;
    case 8:
      return (
        <ChoiceStep
          stepNum={8}
          title="Wen möchtest du kennenlernen?"
          subtitle="Du kannst mehrere wählen."
          fieldKey="interested_in"
          options={INTERESTED_IN}
          isMulti
        />
      );
    case 9:
      return (
        <ChoiceStep
          stepNum={9}
          title="Was suchst du bei QHearts?"
          fieldKey="relationship_type"
          options={RELATIONSHIP}
        />
      );
    case 10:
      return <StepHeight />;
    case 11:
      return (
        <ChoiceStep
          stepNum={11}
          title="Und Kinder?"
          fieldKey="children_status"
          options={CHILDREN}
          skipNext
        />
      );
    case 12:
      return (
        <ChoiceStep
          stepNum={12}
          title="Familienpläne?"
          fieldKey="family_plans"
          options={FAMILY}
          skipNext
        />
      );
    case 13:
      return (
        <ChoiceStep
          stepNum={13}
          title="Glaube."
          subtitle="Was dich prägt — oder nicht."
          fieldKey="faith"
          options={FAITH}
          skipNext
        />
      );
    case 14:
      return (
        <ChoiceStep
          stepNum={14}
          title="Was magst du?"
          subtitle="Wähle bis zu 8."
          fieldKey="interests"
          options={INTERESTS_LIST}
          isMulti
          max={8}
          skipNext
        />
      );
    case 15:
      return (
        <ChoiceStep
          stepNum={15}
          title="Wie würdest du dich beschreiben?"
          subtitle="Wähle bis zu 5."
          fieldKey="traits"
          options={TRAITS_LIST}
          isMulti
          max={5}
          skipNext
        />
      );
    case 16:
      return (
        <TextStep
          stepNum={16}
          title="Was machst du beruflich?"
          fieldKey="profession"
          placeholder="z. B. Ärztin, Designer, Lehrerin"
          skipNext
        />
      );
    case 17:
      return (
        <ChoiceStep
          stepNum={17}
          title="Dein Lifestyle."
          fieldKey="lifestyle"
          options={LIFESTYLE}
          skipNext
        />
      );
    case 18:
      return (
        <ChoiceStep
          stepNum={18}
          title="Höchster Abschluss."
          fieldKey="education"
          options={EDU}
          skipNext
        />
      );
    case 19:
      return (
        <ChoiceStep
          stepNum={19}
          title="Rauchst du?"
          fieldKey="smoking"
          options={SMOKING}
          skipNext
        />
      );
    case 20:
      return (
        <ChoiceStep
          stepNum={20}
          title="Und Alkohol?"
          fieldKey="alcohol"
          options={ALCOHOL}
          skipNext
        />
      );
    case 21:
      return <StepLocation />;
    case 22:
      return <StepBio />;
    case 23:
      return <StepPhotos />;
    case 24:
      return <StepVerifyIntro />;
    case 25:
      return <StepHowVerify />;
    case 26:
      return <StepLegalName />;
    case 27:
      return <StepCamPerm />;
    case 28:
      return <StepSelfie />;
    case 29:
      return <StepVerifyStatus />;
    case 30:
      return <StepReview />;
    case 31:
      return <StepSuccess />;
    default:
      return <StepEntry />;
  }
}

export default function Onboarding() {
  return (
    <OnboardingProvider>
      <Toaster richColors position="top-center" />
      <Wizard />
    </OnboardingProvider>
  );
}
